import { Controller } from "@hotwired/stimulus"
import { auditStyle } from "../utils/a11y_audit"

// StyleAuditController — surfaces non-blocking "Style review" guidance
// alongside the live preview on a niche-template wizard's style step.
// Mounted alongside any existing live-preview controllers, e.g.:
//
//   data-controller="palette-live-preview daily-quote-live-preview style-audit"
//
// What this controller does:
//   * Listens on every form input that drives a live update
//     (`data-live-preview-field`).
//   * Re-runs `auditStyle({...})` from utils/a11y_audit.
//   * Re-renders the panel into the `panel` target with one row per
//     warning + one info row for the current contrast ratio.
//   * Persists per-warning dismissals in `sessionStorage` keyed by
//     `style-audit-dismissed:<configId>` so the same advice doesn't
//     re-pop after the creator has seen it.
//
// What this controller does NOT do:
//   * Block the Save action — every check is guidance.
//   * Mutate any saved state — dismissals are session-local UX.
//   * Re-implement the preview swap logic — palette-live-preview
//     already handles bg image / overlay / tint reactivity.
//
// Stimulus values (set from the wizard root):
//   * fallback-bg-value (string hex) — the "expected canvas colour"
//     under text. Default "#1F2937" (slate-800) — most kit wizards
//     render text over a dark fallback gradient when no background
//     asset is selected, so the audit predicts contrast against that.
//     Daily Quote / Book Takeaway / Phrase List can override per-kit.
//   * config-id-value (string) — used to namespace sessionStorage
//     so dismissals don't leak across configs.
//   * display-base-px-value (number) — base display px BEFORE the
//     font_size_scale multiplier. Default 88 (most kit templates).
//   * body-base-px-value (number) — base body px BEFORE scale.
//     Default 32.
export default class StyleAuditController extends Controller {
  static targets = ["panel"]
  static values = {
    fallbackBg:   { type: String, default: "#1F2937" },
    configId:     { type: String, default: "" },
    displayBasePx: { type: Number, default: 88 },
    bodyBasePx:    { type: Number, default: 32 },
  }

  connect() {
    this.dismissed = this.loadDismissals()
    this.boundChange = this.handleChange.bind(this)

    // Attach to every reactive form field. Both events are bound for
    // sliders + colour pickers (parity with palette-live-preview).
    this.element.querySelectorAll("[data-live-preview-field]").forEach((el) => {
      el.addEventListener("change", this.boundChange)
      if (el.type === "range" || el.type === "color") {
        el.addEventListener("input", this.boundChange)
      }
    })

    // Render once on initial connect so the panel reflects the saved
    // state — not just changed-since-last-load state.
    this.render()
  }

  disconnect() {
    this.element.querySelectorAll("[data-live-preview-field]").forEach((el) => {
      el.removeEventListener("change", this.boundChange)
      el.removeEventListener("input", this.boundChange)
    })
  }

  handleChange() {
    this.render()
  }

  // Reads the four common control values straight off the form,
  // resolves "auto" italic via the picked type-pair card's data
  // attribute, and asks auditStyle for warnings. The audit utility is
  // pure — this controller is a thin coordinator (~50 lines minus the
  // panel chrome).
  computeAudit() {
    const read = (field) => {
      const el = this.element.querySelector(`[data-live-preview-field="${field}"]`)
      if (!el) return null
      // Radio groups: scan the group, return the checked one's value.
      if (el.type === "radio") {
        const checked = this.element.querySelector(
          `[data-live-preview-field="${field}"]:checked`,
        )
        return checked ? checked.value : null
      }
      if (el.type === "checkbox") return el.checked
      return el.value
    }

    const paletteText = read("text_color") || null
    const paletteBg = this.fallbackBgValue

    // Stroke is at the bottom of the advanced details — width may be 0
    // (no painted stroke) or a positive number. Falls through to the
    // audit's "skip if 0" check.
    const strokeWidthRaw = parseFloat(read("text_stroke_width"))
    const strokeWidth = Number.isFinite(strokeWidthRaw) ? strokeWidthRaw : 0
    const strokeColor = read("text_stroke_color") || null

    // Font-size scale multiplies BOTH base sizes — pure math, no DOM
    // read. Caller-configurable via Stimulus values so each template
    // declares its own renderer-pixel constants.
    const scale = parseFloat(read("font_size_scale")) || 1.0
    const displayFontSizePx = this.displayBasePxValue * scale
    const bodyFontSizePx = this.bodyBasePxValue * scale

    const italicOverride = read("italic_override") || "auto"

    // Read the picked type pair's italic default off its radio's data
    // attribute (the typography picker bakes a `data-pair-italic` flag
    // on each card if the consumer sets one). Default to false when
    // missing — most templates have upright pairs.
    const pickedTypePair = this.element.querySelector(
      `[data-live-preview-field="type_pair"]:checked`,
    )
    const pairDefaultDisplayItalic = pickedTypePair
      ? pickedTypePair.dataset.pairItalic === "true"
      : false

    return auditStyle({
      paletteText,
      paletteBg,
      strokeWidth,
      strokeColor,
      displayFontSizePx,
      bodyFontSizePx,
      italicOverride,
      pairDefaultDisplayItalic,
    })
  }

  // Renders the panel HTML. Kept as a single function (no template
  // engine) because the markup is minimal and wizard-specific
  // styling cascades from the host wizard's CSS variables.
  render() {
    if (!this.hasPanelTarget) return
    const { warnings, info } = this.computeAudit()

    const visibleWarnings = warnings.filter((w) => !this.dismissed.has(w.id))
    const dismissedCount = warnings.length - visibleWarnings.length

    const allPass = visibleWarnings.length === 0
    const headerLabel = allPass
      ? "Style review · all checks pass"
      : `Style review · ${visibleWarnings.length} suggestion${visibleWarnings.length === 1 ? "" : "s"}`

    const infoRows = info.map((i) =>
      `<div class="style-audit__row style-audit__row--info">
        <span class="style-audit__icon" aria-hidden="true">●</span>
        <span class="style-audit__label">${escapeHtml(i.label)}</span>
      </div>`
    ).join("")

    const warningRows = visibleWarnings.map((w) =>
      `<details class="style-audit__row style-audit__row--warn" data-warning-id="${escapeHtml(w.id)}">
         <summary>
           <span class="style-audit__icon" aria-hidden="true">▲</span>
           <span class="style-audit__label">${escapeHtml(w.label)}</span>
           <button type="button"
                   class="style-audit__dismiss"
                   data-action="style-audit#dismiss"
                   data-warning-id="${escapeHtml(w.id)}"
                   aria-label="Dismiss this suggestion for the rest of the session">×</button>
         </summary>
         <p class="style-audit__detail">${escapeHtml(w.detail)}</p>
       </details>`
    ).join("")

    const dismissedNote = dismissedCount > 0
      ? `<p class="style-audit__dismissed-note">${dismissedCount} suggestion${dismissedCount === 1 ? "" : "s"} dismissed for this session.</p>`
      : ""

    this.panelTarget.innerHTML = `
      <div class="style-audit ${allPass ? "style-audit--pass" : "style-audit--has-warn"}">
        <p class="style-audit__header">${escapeHtml(headerLabel)}</p>
        <div class="style-audit__rows">
          ${infoRows}
          ${warningRows}
        </div>
        ${dismissedNote}
      </div>
    `
  }

  // Dismiss action — clicked from a per-warning ✕ button. Adds the
  // warning's id to the in-memory + sessionStorage set, then re-renders
  // (which filters that id out).
  dismiss(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = event.currentTarget.dataset.warningId
    if (!id) return
    this.dismissed.add(id)
    this.persistDismissals()
    this.render()
  }

  loadDismissals() {
    try {
      const raw = sessionStorage.getItem(this.storageKey())
      if (!raw) return new Set()
      const arr = JSON.parse(raw)
      return Array.isArray(arr) ? new Set(arr) : new Set()
    } catch (_e) {
      return new Set()
    }
  }

  persistDismissals() {
    try {
      sessionStorage.setItem(
        this.storageKey(),
        JSON.stringify(Array.from(this.dismissed)),
      )
    } catch (_e) {
      // sessionStorage may throw in private mode / quota errors — silent
      // fallback is fine because the in-memory set still works for the
      // current page lifetime.
    }
  }

  storageKey() {
    return `style-audit-dismissed:${this.configIdValue || "default"}`
  }
}

// Tiny HTML-escape helper. Audit messages are static string constants,
// but defence-in-depth: the panel renders via innerHTML so anything
// reaching it must be neutralised.
function escapeHtml(str) {
  if (str == null) return ""
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}
